import { z } from 'zod';
import { baseModelSchema } from '../../../model/baseModel';
import { ITruckTypeFormValues } from './truckTypeFormValues';

/**
 * Zod schema for validating truck type data.
 * Extends the base model schema with truck type-specific fields.
 * Represents a classification of trucks used for transportation.
 *
 * @property {string} code - Unique identifier code (max 5 characters)
 * @property {string} description - Description of the truck type (max 255 characters)
 * @property {number} [defaultNumberOfPallets] - Optional default pallet capacity
 * @property {number} [maxPayload] - Optional maximum payload weight in kilograms
 * @property {number} [weightOfTruck] - Optional weight of the empty truck in kilograms
 * @property {number} [tareWeight] - Optional tare weight in kilograms
 * @property {number} [percentageWarning] - Optional warning threshold percentage
 */
export const truckTypeSchema = baseModelSchema.extend({
    code: z.string().nonempty('Code is required').max(5, 'Code can only be up to 5 characters long.').default(''),
    description: z.string().nonempty('Description is required').max(255, 'Description can only be up to 255 characters long.').default(''),
    // Allow string or number for numeric fields, coerce string to number if possible during parse
    defaultNumberOfPallets: z.preprocess(
        (x) => {
            if (typeof x === 'string') {
                if (x === '' || x === null || x === undefined) return null;
                const parsed = parseFloat(x);
                return isNaN(parsed) ? null : parsed;
            }
            return x;
        },
        z.number().min(0, 'Cannot be negative').optional().nullable()
    ),
    maxPayload: z.preprocess(
        (x) => {
            if (typeof x === 'string') {
                if (x === '' || x === null || x === undefined) return null;
                const parsed = parseFloat(x);
                return isNaN(parsed) ? null : parsed;
            }
            return x;
        },
        z.number().min(0, 'Cannot be negative').optional().nullable()
    ),
    weightOfTruck: z.preprocess(
        (x) => {
            if (typeof x === 'string') {
                if (x === '' || x === null || x === undefined) return null;
                const parsed = parseFloat(x);
                return isNaN(parsed) ? null : parsed;
            }
            return x;
        },
        z.number().min(0, 'Cannot be negative').optional().nullable()
    ),
    tareWeight: z.preprocess(
        (x) => {
            if (typeof x === 'string') {
                if (x === '' || x === null || x === undefined) return null;
                const parsed = parseFloat(x);
                return isNaN(parsed) ? null : parsed;
            }
            return x;
        },
        z.number().min(0, 'Cannot be negative').optional().nullable()
    ),
    percentageWarning: z.preprocess(
        (x) => {
            if (typeof x === 'string') {
                if (x === '' || x === null || x === undefined) return null;
                const parsed = parseFloat(x);
                return isNaN(parsed) ? null : parsed;
            }
            return x;
        },
        z.number().min(0, 'Cannot be negative').optional().nullable()
    ),
});

/**
 * TypeScript interface representing a TruckType object.
 * Generated from the Zod schema for type safety.
 */
export type ITruckType = z.infer<typeof truckTypeSchema>;

/**
 * Creates a validated TruckType object from form values.
 *
 * @param {ITruckTypeFormValues} formValues - The form data to convert
 * @returns {ITruckType} A validated truck type object
 * @throws {ZodError} If validation fails
 */
export function TruckType(formValues : ITruckTypeFormValues) : ITruckType {
    return truckTypeSchema.parse({
        ...formValues,
    });
}
